import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, LogOut, Clock } from "lucide-react";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_RU = { mon: "Понедельник", tue: "Вторник", wed: "Среда", thu: "Четверг", fri: "Пятница", sat: "Суббота", sun: "Воскресенье" };

export default function StaffDashboard() {
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    axios.get(`${API}/team/me`)
      .then((r) => {
        // Staff now uses the shared calendar as the working screen
        navigate(r.data.role === "owner" ? "/dashboard" : "/calendar", { replace: true });
      })
      .catch(() => setError("Не удалось загрузить профиль. Войдите заново."));
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/auth";
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={logout} className="bg-black text-white">Войти</Button>
      </div>
    </div>
  );
  if (!ctx) return <div className="min-h-screen flex items-center justify-center text-gray-400">Загрузка...</div>;

  const staff = ctx.staff || {};
  const schedule = staff.schedule || {};

  return (
    <div className="min-h-screen bg-gray-50 text-black" data-testid="staff-dashboard">
      <header className="bg-white border-b-4 border-black px-4 py-3 flex items-center justify-between">
        <div>
          <img src="/logo-header.png" alt="Chatity" className="h-5 w-auto mb-0.5" data-testid="staff-dash-logo-img" />
          <div className="font-bold">{ctx.salon_name}</div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={ctx.role === "admin" ? "bg-purple-600 text-white" : "bg-emerald-600 text-white"}>
            {ctx.role === "admin" ? "Администратор" : "Мастер"}
          </Badge>
          <button data-testid="staff-logout-btn" onClick={logout} className="p-2 hover:bg-gray-100 rounded" title="Выйти">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="bg-white border-2 border-black rounded-lg p-5">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <span className="w-4 h-4 rounded-full inline-block" style={{ background: staff.color || "#0ea5e9" }} />
            {staff.name}
          </h2>
          {(staff.services || []).length > 0 && (
            <p className="text-sm text-gray-600 mt-1">Услуги: {staff.services.join(", ")}</p>
          )}
        </div>

        <div className="bg-white border-2 border-black rounded-lg p-5" data-testid="staff-schedule-card">
          <h3 className="font-bold flex items-center gap-2 mb-3"><Clock className="w-4 h-4" /> Мой график</h3>
          <div className="divide-y">
            {DAYS.map((d) => {
              const day = schedule[d] || {};
              return (
                <div key={d} className="flex items-center justify-between py-2 text-sm">
                  <span className={day.enabled ? "font-medium" : "text-gray-400"}>{DAY_RU[d]}</span>
                  {day.enabled
                    ? <span className="font-semibold">{day.start} — {day.end}</span>
                    : <span className="text-gray-400">Выходной</span>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border-2 border-black rounded-lg p-5" data-testid="staff-upcoming-card">
          <h3 className="font-bold flex items-center gap-2 mb-3"><CalendarDays className="w-4 h-4" /> Мои ближайшие записи</h3>
          {upcoming.length === 0 && <p className="text-sm text-gray-400">Пока нет предстоящих записей — AI-администратор добавит их сюда автоматически</p>}
          <div className="divide-y">
            {upcoming.map((b) => (
              <div key={b.id} className="py-2.5 text-sm flex items-center justify-between gap-2" data-testid={`staff-booking-${b.id}`}>
                <div>
                  <div className="font-semibold">{(b.start_at || "").replace("T", " ")}</div>
                  <div className="text-gray-600">{b.service} — {b.client_name}</div>
                </div>
                {b.branch_name && <span className="text-xs text-gray-400">📍 {b.branch_name}</span>}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
